import { useMemo, useState, useRef } from 'react'
import { faCoins, faEllipsis } from '@fortawesome/pro-solid-svg-icons'
import { Dropdown, Text, Button } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TransactionDeleteModal } from '@/features/transactions/TransactionDeleteModal'
import { TransactionDetails } from '@/features/transactions/transaction-details/'
import { useFlyoutV2 } from '@/utils/sharedHooks'

interface ITransactionDetailsMenuProps {
  transaction: TransactionOrSplit
  toggleFileModal: () => void
  isManualTransaction: boolean
}

export const TransactionDetailsMenu = ({
  transaction,
  toggleFileModal,
  isManualTransaction,
}: ITransactionDetailsMenuProps) => {
  const { openFlyout } = useFlyoutV2()
  const track = useAnalyticsTrack()
  const [open, setOpen] = useState(false)
  const preventCloseRef = useRef(false)

  const content = useMemo(() => {
    return isManualTransaction
      ? {
          details: 'View/Edit Details',
          button: 'Delete Transaction',
        }
      : {
          details: 'View Details',
          button: 'Mark as Duplicate',
        }
  }, [isManualTransaction])

  const preventCloseAction = (value: boolean) => {
    preventCloseRef.current = value
  }

  const handleOpenTransactionDetails = (activeIndex: number) => {
    // Reset prevent close state when opening
    preventCloseRef.current = false

    openFlyout({
      content: (
        <TransactionDetails
          transactionId={transaction.id}
          parentId={transaction.splitFrom ?? undefined}
          defaultActiveIndex={activeIndex}
          isManualTransaction={isManualTransaction}
          setPreventClose={preventCloseAction}
        />
      ),
      description: 'View details and edit transaction information.',
      title: 'Transaction Details',
      titleIcon: faCoins,
      preventClose: () => {
        const shouldPrevent = preventCloseRef.current
        return shouldPrevent
      },
    })
    track('clicked transaction flyout button')
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Trigger asChild>
          <Button variant="discreet" size="square" iconRight={faEllipsis} />
        </Dropdown.Trigger>

        <Dropdown.Content align="end">
          <Dropdown.Item onClick={() => handleOpenTransactionDetails(0)}>
            <Text>{content.details}</Text>
          </Dropdown.Item>

          <Dropdown.Item onClick={toggleFileModal}>
            <Text>Upload Receipt</Text>
          </Dropdown.Item>

          <Dropdown.Item onClick={() => handleOpenTransactionDetails(1)}>
            <Text>Add Note</Text>
          </Dropdown.Item>

          {(!transaction.reconciliationId || !isManualTransaction) && (
            <>
              <Dropdown.Separator />
              <Dropdown.Item onClick={() => setOpen(true)}>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={
                    isManualTransaction ? TextColors.DANGER : TextColors.MUTED
                  }
                >
                  {content.button}
                </Text>
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Content>
      </Dropdown>

      <TransactionDeleteModal
        open={open}
        close={() => setOpen(false)}
        transactionId={transaction.id}
        isManualTransaction={isManualTransaction}
      />
    </>
  )
}
